import type {
  Collection,
  CollectionTree,
  CollectionQuery,
  CreateCollectionInput,
  UpdateCollectionInput,
  AddModelsToCollectionInput,
  RemoveModelsFromCollectionInput,
  PaginationMeta,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';

// Paginated result interface
export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

// Helper to build query strings
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([_, v]) => v != null);
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams();
  entries.forEach(([k, v]) => {
    if (Array.isArray(v)) {
      // Handle array parameters (e.g., collectionIds[])
      v.forEach((item) => searchParams.append(`${k}[]`, String(item)));
    } else {
      searchParams.append(k, String(v));
    }
  });
  return `?${searchParams.toString()}`;
}

// ============================================================================
// COLLECTIONS API
// ============================================================================

export const collectionsApi = {
  /**
   * Create a new collection
   */
  create: async (input: CreateCollectionInput): Promise<Collection> => {
    const response = await api.post<Collection>('/collections', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create collection');
    }
    return response.data;
  },

  /**
   * List collections with pagination
   */
  list: async (query?: CollectionQuery): Promise<PaginatedResult<Collection>> => {
    const response = await api.get<Collection[]>(`/collections${buildQueryString(query)}`);
    if (!response.success || !response.data || !response.meta?.pagination) {
      throw new Error('Failed to fetch collections');
    }
    return {
      items: response.data,
      pagination: response.meta.pagination,
    };
  },

  /**
   * Get full hierarchical tree structure
   */
  getTree: async (): Promise<CollectionTree[]> => {
    const response = await api.get<CollectionTree[]>('/collections/tree');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch collection tree');
    }
    return response.data;
  },

  /**
   * Get a single collection by ID (with children)
   */
  getById: async (id: string): Promise<CollectionTree> => {
    const response = await api.get<CollectionTree>(`/collections/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch collection');
    }
    return response.data;
  },

  /**
   * Update a collection
   */
  update: async (id: string, input: UpdateCollectionInput): Promise<Collection> => {
    const response = await api.patch<Collection>(`/collections/${id}`, input);
    if (!response.success || !response.data) {
      throw new Error('Failed to update collection');
    }
    return response.data;
  },

  /**
   * Delete a collection (fails if has children)
   */
  delete: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/collections/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete collection');
    }
  },

  // ============================================================================
  // MODEL OPERATIONS
  // ============================================================================

  /**
   * Add models to collection (bulk)
   */
  addModels: async (collectionId: string, input: AddModelsToCollectionInput): Promise<void> => {
    const response = await api.post<void>(`/collections/${collectionId}/models`, input);
    if (!response.success) {
      throw new Error('Failed to add models to collection');
    }
  },

  /**
   * Remove models from collection (bulk)
   */
  removeModels: async (
    collectionId: string,
    input: RemoveModelsFromCollectionInput
  ): Promise<void> => {
    const response = await api.delete<void>(`/collections/${collectionId}/models`, input);
    if (!response.success) {
      throw new Error('Failed to remove models from collection');
    }
  },
};

// Re-export types for convenience
export type { CollectionQuery };
